import {
  DimensionSource,
  IntervalJSON,
  JSONSerializable,
  Source,
} from '@highimpact-frontend/api-types';
import { ErrorStatus } from '@highimpact/dashboard-ui';
import {
  ControlPanel,
  ControlPanelSettings,
  plotPreferencesFromSources,
} from '@highimpact/scatterplot';
import { WidgetSourceSelector } from '@highimpact/source-selector';
import { TimeRangeUnionForm } from '@highimpact/time-interval-selector';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MigratedPlotFormProps } from '../../../../forms/formHandlerShared';
import {
  ensureConfiguredScatterPlot,
  getIntervals,
  isValid,
  setAdditionalProperties,
  setElementIndependentSourcesFromResolvedSources,
  setIntervals,
} from '../../../../state/dashboard-element-operations';
import { getDefaultIndependentDimensionKeys } from '../shared/buildWidgetDataSourceRequest';
import {
  buildIndependentDimensionSelectionFromElement,
  dimensionSelectionToAxisSources,
  resolveIndependentDimensionSelectionFromCatalog,
  syncIndependentDimensionSelectionFromCatalog,
} from '../shared/independentDimensionSelection';
import {
  PlotConfigFormDivider,
  PlotConfigFormShell,
  PlotConfigInvalidStatus,
  PlotConfigSubmitButton,
} from '../shared/plotConfigFormLayout';
import {
  areAxisSourceSelectionsEqual,
  flattenAxisSourceSelection,
  resolveSourcesFromCatalog,
} from '../shared/resolveSourcesFromCatalog';
import { useWidgetSourceSelection } from '../shared/useWidgetSourceSelection';

const SCATTER_DIMENSION_KEYS = getDefaultIndependentDimensionKeys('scatter');
const SCATTER_AXIS_ORDER = ['unit_y', 'unit_x'] as const;
const SCATTER_DIMENSION_MULTIPLE = {
  unit_x: false,
  unit_y: true,
} as const;

function splitScatterSources(sources: readonly Source[]) {
  return {
    unit_x: sources.slice(-1),
    unit_y: sources.slice(0, -1),
  };
}

function ScatterForm({
  config,
  project_id,
  site_id,
  campaigns,
  onSubmit,
  onChange,
}: MigratedPlotFormProps) {
  const scope = useMemo(() => ({ project_id, site_id }), [project_id, site_id]);
  const { loading, error, data } = useWidgetSourceSelection({
    widget: 'scatter',
    scope,
  });

  const initialDimensionSelection = useMemo(
    () =>
      buildIndependentDimensionSelectionFromElement(
        data && data.response_type === 'dimensions' ? data : null,
        config,
        SCATTER_DIMENSION_KEYS,
        splitScatterSources
      ),
    [config, data]
  );

  const [dimensionSelection, setDimensionSelection] = useState<
    Record<string, DimensionSource[]>
  >(initialDimensionSelection);
  const dimensionSelectionRef = useRef(dimensionSelection);
  dimensionSelectionRef.current = dimensionSelection;

  const [submitCount, setSubmitCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [validation, setValidation] = useState(isValid(config));

  useEffect(() => {
    setDimensionSelection(initialDimensionSelection);
  }, [initialDimensionSelection]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setDimensionSelection((previousSelection) => {
      const syncedSelection = syncIndependentDimensionSelectionFromCatalog(
        data,
        SCATTER_DIMENSION_KEYS,
        previousSelection
      );

      return SCATTER_DIMENSION_KEYS.every((dimensionKey) =>
        areAxisSourceSelectionsEqual(
          {
            [dimensionKey]: dimensionSelectionToAxisSources({
              [dimensionKey]: previousSelection[dimensionKey] ?? [],
            })[dimensionKey],
          },
          {
            [dimensionKey]: dimensionSelectionToAxisSources({
              [dimensionKey]: syncedSelection[dimensionKey] ?? [],
            })[dimensionKey],
          }
        )
      )
        ? previousSelection
        : syncedSelection;
    });
  }, [data]);

  const applyDimensionSelection = useCallback(
    (nextSelection: Record<string, DimensionSource[]>) => {
      const configuredElement = ensureConfiguredScatterPlot(config);
      const resolvedSelection = resolveIndependentDimensionSelectionFromCatalog(
        data,
        nextSelection,
        SCATTER_DIMENSION_KEYS
      );
      const resolvedSources = SCATTER_AXIS_ORDER.flatMap(
        (dimensionKey) => resolvedSelection[dimensionKey] ?? []
      );
      const newConfig = setElementIndependentSourcesFromResolvedSources(
        resolvedSelection,
        SCATTER_AXIS_ORDER,
        configuredElement,
        { lastIsX: true }
      );
      const previousPreferences = (
        configuredElement.properties as {
          additional_properties?: ControlPanelSettings;
        }
      ).additional_properties;
      const newPlotPreferences = plotPreferencesFromSources(
        resolvedSources,
        true,
        undefined,
        previousPreferences
      );
      const newConfigWithPreferences = setAdditionalProperties(
        newPlotPreferences as unknown as JSONSerializable,
        newConfig
      );
      setValidation(isValid(newConfigWithPreferences));
      onChange(newConfigWithPreferences);
    },
    [config, data, onChange]
  );

  const scatterControlPanelSettings =
    useMemo((): ControlPanelSettings | null => {
      const unitXCount = dimensionSelection.unit_x?.length ?? 0;
      const unitYCount = dimensionSelection.unit_y?.length ?? 0;

      if (unitXCount === 0 || unitYCount === 0) {
        return null;
      }

      const fromConfig = (
        config.properties as { additional_properties?: ControlPanelSettings }
      ).additional_properties;

      if (fromConfig) {
        return fromConfig;
      }

      const axisSelection = dimensionSelectionToAxisSources(dimensionSelection);
      const flattenedSources = flattenAxisSourceSelection(
        axisSelection,
        SCATTER_AXIS_ORDER
      );
      const resolvedSources = resolveSourcesFromCatalog(
        data,
        flattenedSources.map((source) => ({
          measure_point_id: source.measure_point_id,
          measure_type_id: source.measure_type_id,
        })),
        flattenedSources
      );

      if (resolvedSources.length < 2) {
        return null;
      }

      return plotPreferencesFromSources(
        resolvedSources,
        true
      ) as ControlPanelSettings;
    }, [config.properties, data, dimensionSelection]);

  const handleIntervalsUpdate = (intervals: IntervalJSON[]) => {
    const newConfig = setIntervals(
      intervals,
      ensureConfiguredScatterPlot(config)
    );
    setValidation(isValid(newConfig));
    onChange(newConfig);
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setSubmitCount(submitCount + 1);
    if (validation[0]) {
      setIsSending(true);
      onSubmit(config).finally(() => setIsSending(false));
    }
  };

  const dataSourceErrors = validation[1].dataSource ?? [];
  const dataSourceHasError: ErrorStatus =
    submitCount > 0
      ? { tag: 'Submited', errors: dataSourceErrors }
      : { tag: 'Not submitted' };

  return (
    <PlotConfigFormShell onSubmit={handleSubmit}>
      <WidgetSourceSelector
        data={data}
        loading={loading}
        error={error}
        initialIndependentSelection={dimensionSelection}
        independentDimensionMultiple={SCATTER_DIMENSION_MULTIPLE}
        onIndependentDimensionSelect={(dimensionKey, selected) => {
          const nextSelection = {
            ...dimensionSelectionRef.current,
            [dimensionKey]: selected,
          };
          setDimensionSelection(nextSelection);
          applyDimensionSelection(nextSelection);
        }}
        matchControlPanelStyle
      />

      <PlotConfigInvalidStatus error={dataSourceHasError} />

      <PlotConfigFormDivider />

      <TimeRangeUnionForm
        onChange={handleIntervalsUpdate}
        campaigns={campaigns}
        noDivision
        defaultIntervals={getIntervals(config)}
        matchControlPanelStyle
      />

      {scatterControlPanelSettings ? <PlotConfigFormDivider /> : null}

      {scatterControlPanelSettings && (
        <ControlPanel
          settings={scatterControlPanelSettings}
          plotType="scatter"
          onSettingsChanged={(next) => {
            const newConfig = setAdditionalProperties(
              next as unknown as JSONSerializable,
              ensureConfiguredScatterPlot(config)
            );
            setValidation(isValid(newConfig));
            onChange(newConfig);
          }}
          useRangeSliderControl={false}
        />
      )}

      <PlotConfigSubmitButton
        isSending={isSending}
        loading={loading}
        disabled={
          dataSourceHasError.tag === 'Submited' &&
          dataSourceHasError.errors.length > 0
        }
      />
    </PlotConfigFormShell>
  );
}

export default ScatterForm;
